// Simple user storage system using localStorage
// In a real app, this would connect to a backend database

import { logger } from './logger';

const USERS_KEY = 'meetingsync_users';
const CURRENT_USER_KEY = 'meetingsync_current_user';

export const UserStorage = {
  // Get all users
  getAllUsers() {
    try {
      const users = localStorage.getItem(USERS_KEY);
      return users ? JSON.parse(users) : [];
    } catch (error) {
      logger.error('Error getting users:', error);
      return [];
    }
  },

  // Save a new user or update existing
  saveUser(userData) {
    try {
      const users = this.getAllUsers();
      const existingUserIndex = users.findIndex(u => u.email === userData.email);
      
      if (existingUserIndex >= 0) {
        // Update existing user
        users[existingUserIndex] = { ...users[existingUserIndex], ...userData };
      } else {
        // Add new user
        const newUser = {
          ...userData,
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          isNewUser: true
        };
        users.push(newUser);
      }
      
      localStorage.setItem(USERS_KEY, JSON.stringify(users));
      return users.find(u => u.email === userData.email);
    } catch (error) {
      logger.error('Error saving user:', error);
      return null;
    }
  },

  // Find user by email
  getUserByEmail(email) {
    const users = this.getAllUsers();
    return users.find(u => u.email === email);
  },

  // Find user by ID
  getUserById(userId) {
    const users = this.getAllUsers();
    return users.find(u => u.id === userId);
  },

  // Find user by email and password
  getUserByCredentials(email, password) {
    const users = this.getAllUsers();
    return users.find(u => u.email === email && u.password === password);
  },

  // Set current user
  setCurrentUser(user) {
    try {
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    } catch (error) {
      logger.error('Error setting current user:', error);
    }
  },

  // Get current user
  getCurrentUser() {
    try {
      const user = localStorage.getItem(CURRENT_USER_KEY);
      return user ? JSON.parse(user) : null;
    } catch (error) {
      logger.error('Error getting current user:', error);
      return null;
    }
  },

  // Logout
  logout() {
    localStorage.removeItem(CURRENT_USER_KEY);
  },

  // Mark user as onboarded
  completeOnboarding(email, onboardingData) {
    const users = this.getAllUsers();
    const userIndex = users.findIndex(u => u.email === email);
    
    if (userIndex >= 0) {
      users[userIndex] = {
        ...users[userIndex],
        ...onboardingData,
        isNewUser: false,
        onboardingCompleted: true,
        onboardingCompletedAt: new Date().toISOString()
      };
      localStorage.setItem(USERS_KEY, JSON.stringify(users));
      
      // Update current user if they're the one being onboarded
      const currentUser = this.getCurrentUser();
      if (currentUser && currentUser.email === email) {
        this.setCurrentUser(users[userIndex]);
      }
      
      return users[userIndex];
    }
    return null;
  },

  // Check if user exists
  userExists(email) {
    return !!this.getUserByEmail(email);
  },

  // Update user subscription status
  updateUserSubscriptionStatus(userId, subscriptionData) {
    try {
      const users = this.getAllUsers();
      const userIndex = users.findIndex(u => u.id === userId);
      
      if (userIndex >= 0) {
        users[userIndex] = {
          ...users[userIndex],
          subscription: subscriptionData,
          subscriptionUpdatedAt: new Date().toISOString()
        };
        localStorage.setItem(USERS_KEY, JSON.stringify(users));
        
        // Update current user if they're the one being updated
        const currentUser = this.getCurrentUser();
        if (currentUser && currentUser.id === userId) {
          this.setCurrentUser(users[userIndex]);
        }
        
        return users[userIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating user subscription:', error);
      return null;
    }
  },

  // Get user subscription status
  getUserSubscriptionStatus(userId) {
    const users = this.getAllUsers();
    const user = users.find(u => u.id === userId);
    return user?.subscription || null;
  },

  // Check if user has valid subscription
  hasValidSubscription(userId) {
    const subscription = this.getUserSubscriptionStatus(userId);
    if (!subscription) return false;
    
    return subscription.status === 'active' && 
           subscription.current_period_end > Date.now();
  },

  // Update user profile
  updateUserProfile(userId, profileData) {
    try {
      const users = this.getAllUsers();
      const userIndex = users.findIndex(u => u.id === userId);
      
      if (userIndex >= 0) {
        users[userIndex] = {
          ...users[userIndex],
          ...profileData,
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem(USERS_KEY, JSON.stringify(users));
        
        // Update current user if they're the one being updated
        const currentUser = this.getCurrentUser();
        if (currentUser && currentUser.id === userId) {
          this.setCurrentUser(users[userIndex]);
        }
        
        return users[userIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating user profile:', error);
      return null;
    }
  },

  // Management and company association functions
  
  // Update user company and management information
  updateUserManagementInfo(userId, managementData) {
    try {
      const users = this.getAllUsers();
      const userIndex = users.findIndex(u => u.id === userId);
      
      if (userIndex >= 0) {
        users[userIndex] = {
          ...users[userIndex],
          ...managementData,
          managementUpdatedAt: new Date().toISOString()
        };
        localStorage.setItem(USERS_KEY, JSON.stringify(users));
        
        // Update current user if they're the one being updated
        const currentUser = this.getCurrentUser();
        if (currentUser && currentUser.id === userId) {
          this.setCurrentUser(users[userIndex]);
        }
        
        return users[userIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating user management info:', error);
      return null;
    }
  },

  // Get users by company
  getUsersByCompany(companyName) {
    const users = this.getAllUsers();
    return users.filter(u => u.company && u.company.toLowerCase() === companyName.toLowerCase());
  },

  // Get users by user type and company
  getUsersByTypeAndCompany(userType, companyName) {
    const users = this.getAllUsers();
    return users.filter(u => 
      u.userType === userType && 
      u.company && 
      u.company.toLowerCase() === companyName.toLowerCase()
    );
  },

  // Get all managers
  getAllManagers() {
    const users = this.getAllUsers();
    return users.filter(u => u.userType === 'manager');
  },

  // Get all sales reps
  getAllSalesReps() {
    const users = this.getAllUsers();
    return users.filter(u => u.userType === 'sales');
  },

  // Get users by territory
  getUsersByTerritory(territory) {
    const users = this.getAllUsers();
    return users.filter(u => u.territory && u.territory.toLowerCase() === territory.toLowerCase());
  },

  // Get company list from all users
  getAllCompanies() {
    const users = this.getAllUsers();
    const companies = new Set();
    
    users.forEach(user => {
      if (user.company) {
        companies.add(user.company);
      }
    });
    
    return Array.from(companies).sort();
  },

  // Get territories list from all users
  getAllTerritories() {
    const users = this.getAllUsers();
    const territories = new Set();
    
    users.forEach(user => {
      if (user.territory) {
        territories.add(user.territory);
      }
    });
    
    return Array.from(territories).sort();
  },

  // Check if user is a manager
  isManager(userId) {
    const user = this.getAllUsers().find(u => u.id === userId);
    return user && user.userType === 'manager';
  },

  // Check if user is a sales rep
  isSalesRep(userId) {
    const user = this.getAllUsers().find(u => u.id === userId);
    return user && user.userType === 'sales';
  },

  // Update user territory
  updateUserTerritory(userId, territory) {
    return this.updateUserProfile(userId, { territory });
  },

  // Update user company
  updateUserCompany(userId, company) {
    return this.updateUserProfile(userId, { company });
  },

  // Get user's full management context
  getUserManagementContext(userId) {
    const user = this.getAllUsers().find(u => u.id === userId);
    if (!user) return null;

    return {
      user,
      isManager: user.userType === 'manager',
      isSalesRep: user.userType === 'sales',
      company: user.company,
      territory: user.territory,
      managerId: user.managerId || null,
      lastManagementUpdate: user.managementUpdatedAt
    };
  },

  // Validate password strength
  validatePassword(password) {
    const minLength = 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    
    const errors = [];
    if (password.length < minLength) {
      errors.push(`Password must be at least ${minLength} characters long`);
    }
    if (!hasUpperCase) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!hasLowerCase) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!hasNumbers) {
      errors.push('Password must contain at least one number');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }
};